import { useCallback, useEffect, useState } from 'react';
import { useStyletron } from 'baseui';
import { KIND, SIZE } from 'baseui/button';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import ENGTButton from 'components/UI/ENGTButton/ENGTButton';
import ENGTCheckBox from 'components/UI/ENGTCheckBox/ENGTCheckBox';
import ENGTLink from 'components/UI/ENGTLink/ENGTLink';
import ENGTModalWithoutFooter from 'components/UI/Modal/ModalWithoutFooter/ModalWithoutFooter';

import { contactUsSupportEmailSelector, emailIDSelector } from 'store/App/User/selectors';
import { selectEffectiveWhatsappBusinessId } from 'store/Broadcast/selectors';

import { numberDetailsAPI } from './api';
import {
	getMaxPricingEnrollment,
	isMaxPricingEnabled,
	MAX_PRICING_BUSINESS_ENROLLMENT,
	type MaxPricingEnrollmentDetails,
	unwrapMaxPricingResponse,
} from './MaxPricingEnrollmentData';

enum AgreementStatus {
	NONE = 'none',
	PENDING = 'pending',
	ACCEPTED = 'accepted',
}
interface Enrollment extends MaxPricingEnrollmentDetails {
	partnerEnrollment?: boolean;
	agreementStatus: AgreementStatus;
}
interface IMaxPricingEnrollmentProps {
	primary: boolean;
	onCreateTemplate: () => void;
	onStatusChange: (enabled: boolean | undefined) => void;
}

const MaxPricingEnrollment = ({ primary, onCreateTemplate, onStatusChange }: IMaxPricingEnrollmentProps) => {
	const { t } = useTranslation(['pages', 'common']);
	const [css, theme] = useStyletron();
	const [open, setOpen] = useState<boolean>(false);
	const [busy, setBusy] = useState<boolean>(false);
	const [details, setDetails] = useState<Enrollment | undefined>(undefined);
	const [error, setError] = useState<boolean>(false);
	const [isEulaAccepted, setEulaAccepted] = useState<boolean>(false);
	const [isTermsModalOpen, setTermsModalOpen] = useState<boolean>(false);
	const contactUsSupportEmail = useSelector(contactUsSupportEmailSelector);
	const customerEmail = useSelector(emailIDSelector);
	const metaWabaId = useSelector(selectEffectiveWhatsappBusinessId);

	const text = (key: string) => t(`pages:whatsappManager.numbers.labels.maxPricing.enrollment.${key}`);

	const sectionCss = css({
		backgroundColor: theme.colors.backgroundSecondary,
		border: `0.063rem solid ${theme.colors.backgroundSecondary}`,
		borderRadius: '0.5rem',
		margin: '1rem',
		padding: '1rem',
	});
	const sectionDescriptionCss = css({ color: theme.colors.accent50, fontSize: '0.875rem', lineHeight: '1.25rem' });

	const eulaContainerCss = css({
		backgroundColor: theme.colors.backgroundPrimary,
		border: `0.063rem solid ${theme.colors.accent100}`,
		borderRadius: '0.5rem',
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		gap: '1rem',
		maxHeight: '24rem',
		overflowY: 'auto',
		padding: '1.25rem',
		width: '100%',
	});
	const eulaClauseTitleCss = css({
		color: theme.colors.primary,
		fontSize: '0.9375rem',
		fontWeight: 700,
		marginBottom: '0.25rem',
	});
	const eulaClauseBodyCss = css({
		color: theme.colors.accent50,
		fontSize: '0.875rem',
		lineHeight: '1.375rem',
	});

	const loadStoredDetails = useCallback(async () => {
		setBusy(true);
		setError(false);
		try {
			if (!metaWabaId) {
				throw new Error();
			}
			const result = (await getMaxPricingEnrollment(metaWabaId)) as Enrollment | undefined;
			if (!result?.businessId || !Object.values(AgreementStatus).includes(result.agreementStatus)) {
				throw new Error();
			}
			setDetails(result);
			onStatusChange(isMaxPricingEnabled(result));
		} catch {
			setError(true);
			onStatusChange(undefined);
		} finally {
			setBusy(false);
		}
	}, [metaWabaId, onStatusChange]);

	const refreshDetails = useCallback(async () => {
		setBusy(true);
		setError(false);
		try {
			if (!metaWabaId) {
				throw new Error();
			}
			const result = unwrapMaxPricingResponse<Enrollment>(
				await numberDetailsAPI.refreshMaxPricingDetails(metaWabaId)
			);
			if (!result?.businessId) {
				throw new Error();
			}
			setDetails(result);
			onStatusChange(isMaxPricingEnabled(result));
		} catch {
			setError(true);
		} finally {
			setBusy(false);
		}
	}, [metaWabaId, onStatusChange]);

	useEffect(() => {
		loadStoredDetails();
	}, [loadStoredDetails]);

	const optIn = async () => {
		setBusy(true);
		setError(false);
		try {
			if (!metaWabaId) {
				throw new Error();
			}
			const result = unwrapMaxPricingResponse<{ success: boolean }>(
				await numberDetailsAPI.optInMaxPricing(metaWabaId)
			);
			if (!result?.success) {
				throw new Error();
			}
			setOpen(false);
			await loadStoredDetails();
			numberDetailsAPI.sendMaxPricingOptInEmail(contactUsSupportEmail, customerEmail).catch(() => undefined);
			setError(false);
		} catch {
			setError(true);
		} finally {
			setBusy(false);
		}
	};

	const enrollmentStatus = details?.businessEnrollment?.status;
	let enrollmentStatusText = text('businessUnknown');
	if (enrollmentStatus === MAX_PRICING_BUSINESS_ENROLLMENT.ENROLLED) {
		enrollmentStatusText = text('businessEnrolled');
	} else if (enrollmentStatus === MAX_PRICING_BUSINESS_ENROLLMENT.NOT_ENROLLED) {
		enrollmentStatusText = text('businessNotEnrolled');
	}

	return (
		<>
			{isMaxPricingEnabled(details) ? (
				<ENGTButton
					kind={KIND.primary}
					size={SIZE.compact}
					label={text('createTemplate')}
					onClick={onCreateTemplate}
					dataQa='max-pricing-create-template'
				/>
			) : (
				<ENGTButton
					kind={primary ? KIND.primary : KIND.secondary}
					size={SIZE.compact}
					label={text('button')}
					onClick={() => {
						setOpen(true);
						loadStoredDetails();
					}}
					isLoading={busy && !details}
					dataQa='max-pricing-enrollment-open'
				/>
			)}
			<ENGTModalWithoutFooter
				heading={text('title')}
				modalSubHeading={text('partnerDescription')}
				isOpen={open}
				onClose={() => setOpen(false)}
				width='44rem'
			>
				<div className={css({ display: 'grid', gap: '1rem' })}>
					<ENGTLink
						link={text('learnMore')}
						url='https://developers.facebook.com/documentation/business-messaging/whatsapp/marketing-messages/pricing'
						overrides={{ color: theme.colors.primary, fontSize: '0.875rem', fontWeight: 600 }}
					/>
					{details && (
						<div className={sectionCss}>
							<div
								className={css({
									alignItems: 'flex-start',
									display: 'flex',
									flexDirection: 'column',
									gap: '0.75rem',
								})}
							>
								{!details.partnerEnrollment && (
									<div role='alert' className={sectionDescriptionCss}>
										{text('partnerUnavailable')}
									</div>
								)}
								<div className={sectionDescriptionCss} data-qa='max-pricing-business-enrollment-status'>
									{text('businessEnrollment')}: {enrollmentStatusText}
								</div>
								{details.lastSuccessfulSyncAt && (
									<div className={sectionDescriptionCss} data-qa='max-pricing-last-updated'>
										{text('lastUpdated')}: {new Date(details.lastSuccessfulSyncAt).toLocaleString()}
									</div>
								)}
								{details.stale && (
									<div
										role='status'
										className={css({ color: theme.colors.warning, fontSize: '0.875rem' })}
									>
										{text('stale')}
									</div>
								)}
								<div className={sectionDescriptionCss} data-qa='max-pricing-eula-brief-message'>
									{text('eulaBriefMessage')}
								</div>

								<ENGTCheckBox
									values={{
										isChecked: isEulaAccepted,
										isRequired: true,
										label: (
											<span>
												{text('eulaAgreePrefix')}
												<span
													role='button'
													tabIndex={0}
													onClick={(e) => {
														e.stopPropagation();
														e.preventDefault();
														setTermsModalOpen(true);
													}}
													onKeyDown={(e) => {
														if (e.key === 'Enter' || e.key === ' ') {
															e.stopPropagation();
															e.preventDefault();
															setTermsModalOpen(true);
														}
													}}
													className={css({
														color: theme.colors.primary,
														cursor: 'pointer',
														fontWeight: 600,
														textDecoration: 'underline',
													})}
													data-qa='max-pricing-terms-link'
												>
													{text('termsAndConditions')}
												</span>
											</span>
										),
									}}
									onChange={setEulaAccepted}
									dataQa='max-pricing-eula-acknowledgement'
								/>
								<ENGTButton
									label={text('optIn')}
									onClick={optIn}
									isDisabled={busy || !details.partnerEnrollment || !isEulaAccepted}
									dataQa='max-pricing-opt-in'
								/>
							</div>
						</div>
					)}
					{error && (
						<div role='alert' className={css({ color: theme.colors.negative })}>
							{text('error')}
						</div>
					)}
					<ENGTButton
						className={css({ justifySelf: 'start' })}
						kind={KIND.primary}
						size={SIZE.compact}
						label={text('refreshDetails')}
						onClick={refreshDetails}
						isDisabled={busy || !metaWabaId}
						isLoading={busy}
						dataQa='max-pricing-refresh-details'
					/>
				</div>
			</ENGTModalWithoutFooter>
			<ENGTModalWithoutFooter
				heading={text('eulaHeading')}
				isOpen={isTermsModalOpen}
				onClose={() => setTermsModalOpen(false)}
				width='54rem'
			>
				<div className={css({ display: 'grid', gap: '1rem' })}>
					<div className={eulaContainerCss} data-qa='max-pricing-eula-terms'>
						<div>
							<div className={eulaClauseTitleCss}>{text('eulaClause1Title')}</div>
							<div className={eulaClauseBodyCss}>{text('eulaClause1Body')}</div>
						</div>
						<div>
							<div className={eulaClauseTitleCss}>{text('eulaClause2Title')}</div>
							<div className={eulaClauseBodyCss}>{text('eulaClause2Body')}</div>
						</div>
						<div>
							<div className={eulaClauseTitleCss}>{text('eulaClause3Title')}</div>
							<div className={eulaClauseBodyCss}>{text('eulaClause3Body')}</div>
						</div>
						<div>
							<div className={eulaClauseTitleCss}>{text('eulaClause4Title')}</div>
							<div className={eulaClauseBodyCss}>{text('eulaClause4Body')}</div>
						</div>
					</div>
				</div>
			</ENGTModalWithoutFooter>
		</>
	);
};
export default MaxPricingEnrollment;
